/**
 * Planurile de etaj si survey-ul Wi-Fi (Connection Quality, faza 5) -
 * `SettingsFloorPlansController` din v4/api. Modul nou, fara pereche in portal.
 *
 *   GET  /settings/floor-plans                         lista etajelor firmei
 *   POST /settings/floor-plans                         etaj nou
 *   GET  /settings/floor-plans/{id}                    editorul / vederea unui etaj
 *   POST /settings/floor-plans/{id}                    redenumire, sediu, nivel
 *   POST /settings/floor-plans/{id}/delete             stergere (cu item-uri si survey-uri)
 *   POST /settings/floor-plans/{id}/layout             JSON: calib, walls, items, heat
 *   POST /settings/floor-plans/{id}/image              planul (PNG/JPG), campul `file`
 *   POST /settings/floor-plans/{id}/canvas             desen fara plan / resize (width_m, length_m)
 *   GET  /settings/floor-plans/{id}/survey?survey=     punctele si citirile
 *   POST /settings/floor-plans/{id}/survey             survey nou (device_id)
 *   POST /settings/floor-plans/{id}/survey/{s}/spots   punct nou -> comanda CQW
 *   POST /settings/floor-plans/{id}/survey/{s}/spots/{p}/delete
 *   POST /settings/floor-plans/{id}/survey/{s}/finish
 *   GET  /devices/{id}/floor-position                  "Where is this device"
 */
import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, apiPostForm } from './client'
import type { Calib } from '../floorplan/geometry'
import type { Material } from '../floorplan/predict'

export type Office = { id: number; name: string }

export type FloorSummary = {
  id: number
  office_id: number | null
  name: string
  level_number: number | null
  has_image: boolean
  /** Desenat fara plan: fara imagine, cu marime (`image_w_px`/`image_h_px`) si scara fixa. */
  canvas?: boolean
  image_url: string | null
  image_w_px: number | null
  image_h_px: number | null
  scale_px_per_m: number | null
  updated_at: string | null
}

export type FloorList = {
  floors: FloorSummary[]
  offices: Office[]
  canWrite: boolean
  limits: { max_mb: number; max_side: number }
}

export type Wall = { x1: number; y1: number; x2: number; y2: number; m: Material }

export type ApAttrs = { band?: '2.4' | '5' | '6'; tx_dbm?: number; height_m?: number }

export type FloorItem = {
  kind: 'ap' | 'device' | 'note'
  ap_group_key: string | null
  device_id: number | null
  label: string | null
  x_cm: number
  y_cm: number
  attrs: ApAttrs | null
}

export type Heat = { cell_m: number; mask_m: number; good: number; fair: number; poor: number }

export type FloorAp = {
  key: string
  keys: string[]
  name: string | null
  office_id: number | null
  bssids: string[]
  clients: number
  median_rssi: number | null
  median_retry_pct: number | null
  weak: boolean
  sticky: Array<{ id: number; name: string; rssi: number | null }>
}

export type InventoryDevice = { id: number; name: string; type: string | null; office_id: number | null }

export type DeviceSignal = { at: string; rssi_dbm: number | null; bssid: string | null; ssid: string | null; band: string | null }

export type SurveySummary = { id: number; device_id: number; started_at: string; finished_at: string | null; by_user: number; spots: number }

export type FloorPage = {
  floor: FloorSummary & { calib: Calib | null; walls: Wall[]; heat: Heat }
  items: FloorItem[]
  aps: FloorAp[]
  apThresholds: { weak_rssi: number; weak_retry_pct: number; sticky_rssi: number; sticky_minutes: number }
  apHours: number
  devices: InventoryDevice[]
  signals: Record<string, DeviceSignal>
  surveys: SurveySummary[]
  surveyDevices: Array<{ id: number; name: string; last: string }>
  offices: Office[]
  materials: Material[]
  canWrite: boolean
}

export type SurveyReading = {
  bssid: string
  ssid: string | null
  band: string | null
  channel: number | null
  rssi_dbm: number
  rssi_min: number | null
  rssi_max: number | null
  samples: number
  connected: boolean
}

export type SurveySpot = {
  id: number
  survey_id: number
  x_cm: number
  y_cm: number
  requested_at: string
  measured_at: string | null
  status: 'pending' | 'ok' | 'error' | 'timeout'
  error: string | null
  readings: SurveyReading[]
}

export type SurveyPage = {
  surveys: SurveySummary[]
  selected: number[]
  open: number | null
  spots: SurveySpot[]
  bssidAps: Record<string, { key: string; name: string | null; known: boolean }>
}

export type Layout = {
  calib: Calib | null
  walls: Wall[]
  items: FloorItem[]
  heat: Heat
}

/** Un AP pus pe acelasi etaj cu aparatul (pe mini-plan). */
export type FloorPositionAp = { key: string; prefix: string; name: string; x_cm: number; y_cm: number }

export type FloorPosition = {
  placements: Array<{ floor: FloorSummary; office: string | null; x_cm: number; y_cm: number; aps?: FloorPositionAp[] }>
  /** Toate etajele aparatului, si cele fara plan; gol fara dreptul de a le deschide. */
  floors?: Array<{ id: number; name: string }>
  canOpen: boolean
}

const BASE = '/settings/floor-plans'

export const floorListQuery = queryOptions({
  queryKey: ['settings', 'floor-plans'],
  queryFn: () => apiGet<FloorList>(BASE),
  staleTime: 30_000,
})

export function floorPageQuery(id: number) {
  return queryOptions({
    queryKey: ['settings', 'floor-plans', id],
    queryFn: () => apiGet<FloorPage>(`${BASE}/${id}`),
    staleTime: 60_000,
  })
}

export function surveyQuery(floorId: number, surveyIds: number[]) {
  return queryOptions({
    queryKey: ['settings', 'floor-plans', floorId, 'survey', surveyIds.join(',')],
    queryFn: () => apiGet<SurveyPage>(`${BASE}/${floorId}/survey${surveyIds.length ? `?survey=${surveyIds.join(',')}` : ''}`),
    staleTime: 5_000,
    /* Cat asteapta un punct raspunsul laptopului, se intreaba des. */
    refetchInterval: (q) => (q.state.data?.spots.some((s) => s.status === 'pending') ? 3_000 : false),
  })
}

export function deviceFloorPositionQuery(deviceId: number) {
  return queryOptions({
    queryKey: ['device', deviceId, 'floor-position'],
    queryFn: () => apiGet<FloorPosition>(`/devices/${deviceId}/floor-position`),
    staleTime: 5 * 60_000,
  })
}

export function createFloor(fields: { name: string; office_id: number | null; level_number: number | null }) {
  return apiPost<FloorList & { id: number }>(BASE, {
    name: fields.name,
    office_id: fields.office_id ?? '',
    level_number: fields.level_number ?? '',
  })
}

export function updateFloor(id: number, fields: { name: string; office_id: number | null; level_number: number | null }) {
  return apiPost<FloorList>(`${BASE}/${id}`, {
    name: fields.name,
    office_id: fields.office_id ?? '',
    level_number: fields.level_number ?? '',
  })
}

export function deleteFloor(id: number) {
  return apiPost<FloorList>(`${BASE}/${id}/delete`, {})
}

export function uploadFloorImage(id: number, file: File) {
  const body = new FormData()
  body.append('file', file)
  return apiPostForm<FloorPage>(`${BASE}/${id}/image`, body)
}

/** "Draw without a plan" / "Resize canvas": etajul devine o foaie de `width` x `length` metri. */
export function drawFloorCanvas(id: number, widthM: number, lengthM: number) {
  return apiPost<FloorPage>(`${BASE}/${id}/canvas`, { width_m: widthM, length_m: lengthM })
}

/** Salvarea editorului. `keepalive` la plecarea din pagina (autosave.ts). */
export function saveLayout(id: number, layout: Partial<Layout>, keepalive = false) {
  return apiGet<{ saved: boolean; scale_px_per_m: number | null; updated_at: string | null }>(`${BASE}/${id}/layout`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(layout),
    keepalive,
  })
}

export function startSurvey(floorId: number, deviceId: number) {
  return apiPost<SurveyPage>(`${BASE}/${floorId}/survey`, { device_id: deviceId })
}

export function addSurveySpot(floorId: number, surveyId: number, x_cm: number, y_cm: number) {
  return apiPost<SurveyPage & { spot: number }>(`${BASE}/${floorId}/survey/${surveyId}/spots`, { x_cm, y_cm })
}

export function deleteSurveySpot(floorId: number, surveyId: number, spotId: number) {
  return apiPost<SurveyPage>(`${BASE}/${floorId}/survey/${surveyId}/spots/${spotId}/delete`, {})
}

export function finishSurvey(floorId: number, surveyId: number) {
  return apiPost<SurveyPage>(`${BASE}/${floorId}/survey/${surveyId}/finish`, {})
}

/* ------------------------------------------------------------------------
 * Sugestiile de pozitie: AP-urile din survey, aparatele din amprenta Wi-Fi.
 *
 *   GET  /settings/floor-plans/{id}/suggestions
 *   POST /settings/floor-plans/{id}/suggestions/dismiss   kind=ap|device|reset
 *   POST /settings/floor-plans/{id}/locate                device_ids (optional)
 * --------------------------------------------------------------------- */

export type ApSuggestion =
  | { key: string; name: string | null; status: 'ok'; x_cm: number; y_cm: number; radius_cm: number; spots: number; best_rssi: number }
  | { key: string; name: string | null; status: 'not_enough_data'; spots: number; best_rssi: number | null }

export type DeviceSuggestion = {
  device_id: number
  name: string
  spot_id: number
  requested_at: string
  measured_at: string | null
  measurement: 'pending' | 'ok' | 'error' | 'timeout'
  error: string | null
  status: 'ok' | 'not_enough_data' | 'pending' | 'error' | 'timeout'
  x_cm?: number
  y_cm?: number
  radius_cm?: number
  common_aps?: number
  distance_db?: number
  confidence?: 'high' | 'medium' | 'low'
}

export type Dismissed = { aps: string[]; devices: Record<string, number> }

export type SuggestionsPage = {
  surveySpots: number
  aps: ApSuggestion[]
  devices: DeviceSuggestion[]
  dismissed: Dismissed
  candidates: Array<{ id: number; name: string }>
  message: string | null
  located?: number
}

export function suggestionsQuery(floorId: number) {
  return queryOptions({
    queryKey: ['settings', 'floor-plans', floorId, 'suggestions'],
    queryFn: () => apiGet<SuggestionsPage>(`${BASE}/${floorId}/suggestions`),
    staleTime: 10_000,
    /* Cat asteapta o localizare raspunsul aparatului, se intreaba des. */
    refetchInterval: (q) => (q.state.data?.devices?.some((d) => d.status === 'pending') ? 3_000 : false),
  })
}

export function locateDevices(floorId: number, deviceIds: number[] = []) {
  return apiPost<SuggestionsPage>(`${BASE}/${floorId}/locate`, deviceIds.length ? { device_ids: deviceIds.join(',') } : {})
}

export function dismissSuggestion(floorId: number, s: { kind: 'ap'; key: string } | { kind: 'device'; device_id: number; spot_id: number } | { kind: 'reset' }) {
  return apiPost<SuggestionsPage>(`${BASE}/${floorId}/suggestions/dismiss`, s)
}
